import { useState, useEffect, useMemo, useRef } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { observer } from 'mobx-react-lite';
import styled from '@emotion/styled';
import { keyframes } from '@emotion/react';
import { subscriptionStore } from '@/stores/SubscriptionStore';
import { paywallFlowStore } from '@/stores/PaywallFlowStore';
import { isApplePayCancelledError, useTrialPayment } from '@/hooks/useTrialPayment';
import { usePolicyText } from '@/hooks/usePolicyText';
import { sendLoadSuccess, sendPaymentFailed } from '@/hooks/useWebViewMessaging';
import { CtaButton } from '@/components/ui/CtaButton';
import { PaymentError } from '@/components/ui/PaymentError';
import { RestoreSubscription } from '@/components/paywall/RestoreSubscription';
import { FILM_POSTERS, BENEFIT_ICONS } from '@/constants/films';
import { syncPaywallMetaFromUrl } from '@/utils/paywallUrlParams';

const PAGE_BG = 'rgb(17, 18, 17)';

/** Внешний фон на весь экран — тот же тёмный цвет, что и в колонке (как в v4) */
const PageOuter = styled.div`
  width: 100%;
  min-height: 100vh;
  min-height: 100svh;
  background: ${PAGE_BG};
`;

/** Колонка с ограничением ширины как в v1/v2 */
const PageColumn = styled.div`
  position: relative;
  width: 100%;
  max-width: 430px;
  margin: 0 auto;
  min-height: 100svh;
  padding-top: var(--app-safe-area-top);
  padding-bottom: var(--app-safe-area-bottom);
  background: ${PAGE_BG};
`;

const Screen = styled.div`
    position: relative;
    width: 100%;
    max-width: 430px;
    margin: 0 auto;
    overflow: hidden;
    display: flex;
    flex-direction: column;
    background: ${PAGE_BG};
    z-index: 1;
    min-height: 100svh;
    min-height: var(--app-safe-area-viewport-height);
`;

const PageBg = styled.div`
    position: absolute;
    inset: 0;
    background: ${PAGE_BG};
    z-index: 0;
`;

/* Карусель с затемнением снизу для плавного перехода в контент */
const CarouselSection = styled.div`
    position: relative;
    width: 100%;
    height: 30vh;
    background: ${PAGE_BG};
`;

const CarouselFade = styled.div`
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    height: 64px;
    background: linear-gradient(to top, ${PAGE_BG}, transparent);
    pointer-events: none;
    z-index: 1;
`;

const scrollDown = keyframes`
    from {
        transform: translateY(0);
    }
    to {
        transform: translateY(-50%);
    }
`;

const CarouselWrap = styled.div`
    width: 100%;
    height: 100%;
    overflow: hidden;
`;

const CarouselTrack = styled.div`
    display: flex;
    flex-direction: column;
    animation: ${scrollDown} 25s linear infinite;
`;

const PostersGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 8px;
    padding: 16px;
    flex-shrink: 0;
`;

const Poster = styled.img`
    width: 100%;
    aspect-ratio: 2/3;
    object-fit: cover;
    border-radius: 16px;
`;

const VpnSection = styled.section`
    padding: 0 16px 24px;
    flex-shrink: 0;
`;

const VpnTitle = styled.h2`
    margin: 0 0 16px;
    font-weight: 700;
    font-size: 24px;
    line-height: 1.3;
    text-align: center;
    color: #fff;
`;

const BenefitsRow = styled.div`
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 12px;
`;

const BenefitCard = styled.div`
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 12px 14px;
    border: 1px solid #78797a;
    border-radius: 12px;
`;

const BenefitIcon = styled.img`
    width: 28px;
    height: 28px;
    flex-shrink: 0;
`;

const BenefitText = styled.span`
    font-size: 12px;
    font-weight: 500;
    color: #fff;
`;

const CtaSection = styled.section`
    padding: 0 16px 16px;
    margin-top: auto;
`;

const TrialOffer = styled.p`
    margin: 0 0 4px;
    line-height: 1.4em;
    font-size: 22px;
    font-weight: 500;
    color: #fff;
    text-align: center;
`;

const GreenAccent = styled.span`
    color: rgb(13, 240, 40);
    font-weight: 600;
`;

const PriceNote = styled.p`
    margin: 0 0 16px;
    font-size: 14px;
    color: #9ca3af;
    text-align: center;
`;

const FreeConfirm = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 6px;
    margin-top: 12px;
    font-size: 14px;
    color: rgb(13, 240, 40);
    font-weight: 500;
`;

const CheckIcon = styled.img`
    width: 18px;
    height: 18px;
`;

const Legal = styled.section`
    font-size: 12px;
    line-height: 1.45;
    color: rgb(80, 80, 80);
    text-align: justify;

    a {
        color: rgb(120, 120, 120);
        text-decoration: underline;
    }
`;

const FooterSection = styled.div`
    padding-top: 8px;

    a {
        color: #31bcf9;
    }
`;

/** Прижимает основной контент (VPN + CTA) к низу экрана */
const ContentPush = styled.div`
    margin-top: auto;
`;

/** Обёртка страницы: экран + футер */
const PageLayout = styled.div`
    z-index: 1;
`;

/** Обёртка футера: Legal + Restore */
const FooterWrap = styled.div`
    position: relative;
    z-index: 1;
    color: white;
    padding: 0 16px 16px;
`;

export const PaywallV5 = observer(() => {
  const [searchParams] = useSearchParams();
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState('');
  const [isPageLoading, setIsPageLoading] = useState(true);
  const applePayLoadStatusReportedRef = useRef(false);

  const {
    initializeApplePay,
    showApplePay,
    preferredPaymentMethod,
    isInitializing,
    isApplePayReady,
    trialPlan,
    trialPlans,
    selectTrialPlan,
    refreshPaymentRequest,
    handlePaymentError,
  } = useTrialPayment();

  const {
    trialDays,
    recurringPriceFormatted,
    billingFrequencyLabel,
  } = usePolicyText();

  useEffect(() => {
    const init = async () => {
      if (!subscriptionStore.plans || subscriptionStore.plans.length === 0) {
        await subscriptionStore.fetchPlans();
      }

      const applePayReady = await initializeApplePay();

      if (!applePayReady) {
        const errorMsg = 'Apple Pay is not available on this device. Please use Safari on iOS or macOS.';
        setError(errorMsg);
      }

      if (!applePayLoadStatusReportedRef.current) {
        applePayLoadStatusReportedRef.current = true;
        if (applePayReady) {
          sendLoadSuccess();
        }
        else {
          sendPaymentFailed('Apple Pay is not available on this device. Please use Safari on iOS or macOS.');
        }
      }

      setIsPageLoading(false);

      syncPaywallMetaFromUrl(searchParams);
    };

    void init();
  }, [initializeApplePay, searchParams]);

  const weeklyTrialPlan = useMemo(() => {
    return (
      trialPlans.find((plan) => plan.billingFrequency === 'weekly')
      ?? trialPlan
      ?? null
    );
  }, [trialPlans, trialPlan]);

  useEffect(() => {
    if (!weeklyTrialPlan?.planId) return;
    if (weeklyTrialPlan.planId !== trialPlan?.planId) {
      selectTrialPlan(weeklyTrialPlan.planId);
    }
  }, [weeklyTrialPlan?.planId, trialPlan?.planId, selectTrialPlan]);

  useEffect(() => {
    if (!trialPlan?.planId) return;
    void refreshPaymentRequest();
  }, [trialPlan?.planId, refreshPaymentRequest]);

  const onCta = () => {
    if (processing || isInitializing || !isApplePayReady) return;

    if (!preferredPaymentMethod) {
      const errorMsg = 'Apple Pay is not available on this device. Please use Safari on iOS or macOS.';
      setError(errorMsg);
      sendPaymentFailed(errorMsg);
      return;
    }

    setError('');

    setProcessing(true);
    showApplePay(paywallFlowStore.meta.trim() || undefined)
      .catch((err: unknown) => {
        if (isApplePayCancelledError(err)) {
          return;
        }

        console.error('Payment process failed:', err);
        setError(handlePaymentError(err));
      })
      .finally(() => {
        setProcessing(false);
      });
  };

  return (
    <PageOuter>
      <PageColumn>
        <PageBg />
        <PageLayout>
          <Screen>
            <CarouselSection>
              <CarouselWrap>
                <CarouselTrack>
                  <PostersGrid>
                    {FILM_POSTERS.map((src, i) => (
                      <Poster key={i} src={src} alt="" loading="lazy" />
                    ))}
                  </PostersGrid>
                  <PostersGrid aria-hidden="true">
                    {FILM_POSTERS.map((src, i) => (
                      <Poster key={`dup-${i}`} src={src} alt="" loading="lazy" />
                    ))}
                  </PostersGrid>
                </CarouselTrack>
              </CarouselWrap>
              <CarouselFade />
            </CarouselSection>
            <ContentPush>
              <VpnSection>
                <VpnTitle>Используйте VPN для разблокировки стриминга</VpnTitle>
                <BenefitsRow>
                  <BenefitCard>
                    <BenefitIcon src={BENEFIT_ICONS.device} alt="" aria-hidden />
                    <BenefitText>Используйте до 10 устройств</BenefitText>
                  </BenefitCard>
                  <BenefitCard>
                    <BenefitIcon src={BENEFIT_ICONS.flash} alt="" aria-hidden />
                    <BenefitText>Быстрое и стабильное подключение</BenefitText>
                  </BenefitCard>
                </BenefitsRow>
              </VpnSection>
              <CtaSection>
                <TrialOffer>
                  {trialDays}
                  -дневная
                  {' '}
                  <GreenAccent>бесплатная</GreenAccent>
                  {' '}
                  пробная версия
                </TrialOffer>
                <PriceNote>
                  Затем
                  {' '}
                  {recurringPriceFormatted}
                  {' '}
                  /
                  {billingFrequencyLabel}
                </PriceNote>
                <CtaButton
                  variant="green"
                  loading={processing || isInitializing || isPageLoading}
                  disabled={processing || isInitializing || !isApplePayReady || isPageLoading}
                  onClick={onCta}
                >
                  Продолжить
                </CtaButton>
                <PaymentError error={error} variant="dark" />
                <FreeConfirm>
                  <CheckIcon src="/films/green_check_icon-CcB1kosk.svg" alt="" aria-hidden />
                  Сегодня оплата не требуется!
                </FreeConfirm>

              </CtaSection>
            </ContentPush>
          </Screen>
          <FooterWrap>
            <Legal>
              Продолжая, вы оформляете подписку, которая автоматически продлевается в конце выбранного
              периода,
              если не отменена как минимум за 24 часа до окончания текущего периода. Чтобы избежать списания
              средств, пожалуйста, свяжитесь с нашей службой поддержки для отмены как минимум за 24 часа до
              окончания периода. Если вы не уверены, как отменить подписку, обратитесь к нашим
              {' '}
              <Link to="/policy/terms">Условиям использования</Link>
              . Вы можете сохранить скриншот этой
              информации
              для справки. Продолжая, вы также соглашаетесь с нашей
              {' '}
              <Link to="/policy/privacy">Политикой конфиденциальности приложения</Link>
              ,
              {' '}
              <Link to="/policy/privacy">Политикой конфиденциальности веб-сайта</Link>
              {' '}
              и
              {' '}
              <Link to="/policy/terms">Условиями использования</Link>
              .
            </Legal>
            <FooterSection>
              <RestoreSubscription />
            </FooterSection>
          </FooterWrap>
        </PageLayout>
      </PageColumn>
    </PageOuter>
  );
});
